import React from 'react';
import { useContractTracking, TrackedContract } from '../contexts/ContractTrackingContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  colors: any;
}

const TrackedContractsPanel: React.FC<Props> = ({ isOpen, onClose, colors }) => {
  const { trackedContracts, removeContract, refreshStatus, isRefreshing } = useContractTracking();

  const handleRefresh = () => {
    void refreshStatus(true);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 16,
        top: 60,
        width: 360,
        maxHeight: '80vh',
        background: 'var(--elev)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        boxShadow: 'var(--lift-3)',
        fontFamily: 'var(--font-ui)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--hair)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
      }}>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--txt)' }}>
          ⭐ Tracked Contracts ({trackedContracts.length}/50)
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="mmec-btn is-ghost"
            style={{ color: 'var(--acc)', fontSize: 14 }}
          >
            {isRefreshing ? '⏳' : '🔄'}
          </button>
          <button
            onClick={onClose}
            className="mmec-btn is-ghost"
            style={{ fontSize: 16 }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ overflow: 'auto', flex: 1, padding: 8 }}>
        {trackedContracts.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            No contracts tracked yet.<br />
            Click ⭐ on any contract row to start tracking.
          </div>
        ) : (
          trackedContracts.map((c: TrackedContract) => (
            <div
              key={c.id}
              style={{
                padding: '8px 10px',
                margin: '4px 0',
                borderRadius: 8,
                background: 'var(--elev2)',
                border: '1px solid var(--hair)',
                fontSize: 12.5,
                color: 'var(--txt)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div>
                <strong>{c.symbol}</strong>{' '}
                <span style={{ color: c.optionType === 'C' ? 'var(--up)' : 'var(--down)', fontWeight: 600 }}>
                  {c.optionType === 'C' ? 'CALL' : 'PUT'}
                </span>{' '}
                <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>${c.strike}</span>{' '}
                <span style={{ color: 'var(--muted)' }}>{c.expiry?.slice(0, 10)}</span>
                {c.lastPrice ? (
                  <div style={{ fontSize: 11, color: 'var(--txt-2)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                    Last: ${c.lastPrice?.toFixed(2)} | Vol: {c.lastVolume} | Prem: ${(c.lastPremium || 0).toLocaleString()}
                  </div>
                ) : null}
              </div>
              <button
                onClick={() => removeContract(c.id)}
                className="mmec-btn is-ghost"
                style={{ fontSize: 14, padding: '0 6px' }}
                title="Remove from tracking"
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TrackedContractsPanel;
